import {
  ApiResponse,
  defaultFetch,
  httpMethod,
} from "@/scripts/Common/api";

import {
  getToken,
  PaginationRequest,
} from "@/scripts/Common";

import {
  Card,
  CardManager,
  ProfipayCardStates,
  SearchFilterParameters,
} from "@/components/cards/scripts/types";

import {
  DataTableOptions
} from "@/composables";

import {
  Ref
} from "vue";

import {
  fetchCustomers
} from "@/components/customers/scripts";

import {
  fetchOperator
} from "@/components/operator/scripts/requests";

import {
  CardManagerType
} from "./types";

import {
  fetchIssuers
} from "@/components/issuers/scripts/issuers/requests";

const checkCard = async (cardNumber: number): Promise<ApiResponse<ProfipayCardStates[]>> =>
  await defaultFetch({
    apiName: 'getProfipayCardStates',
    bearer: getToken(),
    body: JSON.stringify({
      cardNumbers: [cardNumber]
    }),
    options: {
      method: httpMethod.POST
    }
  });

const fetchCards = async (options: Ref<DataTableOptions>, queryParams: SearchFilterParameters) => {
  options.value.loading = true;

  const {
    error,
    paginationCount,
    totalItemCount,
    response,
  } = await defaultFetch({
    apiName: 'getCardList',
    bearer: getToken(),
    options: {
      query: {
        pageNumber: (options.value.pagination.page - 1),
        pageSize: options.value.pagination.itemsPerPage,
        cardNumber: queryParams.cardNumber,
        state: queryParams.cardState,
        sortColumn: options.value.sorting.sortBy,
        sortDirection: options.value.sorting.sortDesc ? 'desc' : 'asc',
        managerTypes: queryParams.managerTypes,
        managerName: queryParams.managerName
      }
    }
  });

  options.value.loading = false;

  options.value.pagination.pageCount = paginationCount ?? 0;
  options.value.pagination.totalItemCount = totalItemCount ?? 0;

  return {
    response,
    paginationCount,
    totalItemCount,
    error
  }
};

const updateOrCreateCardList = async (method: httpMethod, cards: Card[]) => {
  const {
    error,
    response,
  } = await defaultFetch({
    apiName: 'updateOrCreateCards',
    bearer: getToken(),
    options: {
      method,
    },
    body: JSON.stringify({
      cardDtoList: cards
    }),
  });

  return{
    response,
    error
  }
}

const deleteCards = async (cardIds: number[]) => {
  const {
    error,
    response,
  } = await defaultFetch({
    apiName: 'deleteCards',
    bearer: getToken(),
    options: {
      method: httpMethod.POST
    },
    body: JSON.stringify({
      cardIdList: cardIds
    }),
  });

  return {
    response,
    error
  }
}

const loadCardStates = async (cardNumbers: number[], paginationRequest: PaginationRequest) => {
  const {
    error,
    response,
  } = await defaultFetch({
    apiName: 'getProfipayCardStates',
    bearer: getToken(),
    body: JSON.stringify({
      cardNumbers: cardNumbers
    }),
    options: {
      method: httpMethod.POST,
      query: {
        pageNumber: (paginationRequest.page - 1),
        pageSize: paginationRequest.itemsPerPage,
      }
    }
  });

  return {
    response,
    error
  }
}

/**
 * Searches for card managers across different types. Results of different types are combined and returned.
 * @param types - Array of CardManagerType to search through
 * @param searchText - Text to filter managers by name
 * @returns Promise containing array of matching CardManager objects
 */
const searchCardManagers = async (types: CardManagerType[], searchText: string): Promise<CardManager[]> => {
  // Array to store all API calls
  const apiCalls = [];

  // Search customers if specified in types
  if (types.includes(CardManagerType.Customer)) {
    apiCalls.push(
      fetchCustomers({ companyName: searchText })
        .then(({ response }) => response?.map(c => ({
          id: c.id,
          name: c.companyName!,
          type: CardManagerType.Customer
        })) || [])
    );
  }

  // Search issuers if specified in types
  if (types.includes(CardManagerType.Issuer)) {
    apiCalls.push(
      fetchIssuers({ companyName: searchText })
        .then(({ response }) => response?.map(i => ({
          id: i.Id!,
          name: i.CompanyName!,
          type: CardManagerType.Issuer
        })) || [])
    );
  }

  // Search operator if specified in types
  // Note: There is always only one operator in the system
  if (types.includes(CardManagerType.Operator)) {
    apiCalls.push(
      fetchOperator()
        .then(({ response }) => {
          // as there is always only one operator, we can just check if the response matches the search text
          if (response && (!searchText || response.companyName.toLowerCase().includes(searchText.toLowerCase()))) {
            return [{
              id: response.id,
              name: response.companyName!,
              type: CardManagerType.Operator
            }];
          }
          return [];
        })
    );
  }

  // Execute all API calls in parallel and combine results
  const results = await Promise.all(apiCalls);
  return results.flat();
};

export {
  checkCard,
  fetchCards,
  updateOrCreateCardList,
  deleteCards,
  loadCardStates,
  searchCardManagers
}
